import React, { type ReactNode } from 'react';

export interface AuthLayoutProps {
  /**
   * Application or Portal Name to display next to the logo
   */
  portalName: string;

  /**
   * Path to the logo image
   */
  logo: string;

  /**
   * Path to the background hero image. Default: "/loginhero.jpg"
   */
  heroImage?: string;

  /**
   * First line of the hero title
   */
  heroTitle1: string;

  /**
   * Second line of the hero title (usually highlighted)
   */
  heroTitle2: string;

  /**
   * Subtitle text below the hero title
   */
  heroSubtitle: string;

  /**
   * Header title for the form section (e.g. "Welcome Back" or "Reset Password")
   */
  formHeaderTitle: string;

  /**
   * Subtitle text below the form header
   */
  formHeaderSubtitle: string;

  /**
   * Copyright text for the footer
   */
  copyrightText: string;

  /**
   * "Privacy Policy" link text for the footer
   */
  privacyPolicyText: string;

  /**
   * "Terms of Service" link text for the footer
   */
  termsOfServiceText: string;

  /**
   * Actions to place in the top right corner (e.g. ThemeSwitcher, LanguageSwitcher)
   */
  topRightActions?: ReactNode;

  /**
   * Form content to render inside the white card on the right
   */
  children: ReactNode;
}

export const AuthLayout: React.FC<AuthLayoutProps> = ({
  portalName,
  logo,
  heroImage = '/loginhero.jpg',
  heroTitle1,
  heroTitle2,
  heroSubtitle,
  formHeaderTitle,
  formHeaderSubtitle,
  copyrightText,
  privacyPolicyText,
  termsOfServiceText,
  topRightActions,
  children,
}) => {
  return (
    <div className="min-h-screen flex w-full bg-background dark:bg-linear-to-br dark:from-slate-900 dark:via-indigo-950/20 dark:to-slate-900 relative overflow-hidden">
      {/* Top Right Actions */}
      {topRightActions && (
        <div className="absolute top-4 ltr:right-4 rtl:left-4 z-50 flex items-center gap-2">
          {topRightActions}
        </div>
      )}

      {/* Background Texture for Right Panel */}
      <div className="absolute inset-0 z-0 opacity-[0.03] dark:opacity-[0.1] pointer-events-none bg-[radial-gradient(#4f46e5_1px,transparent_1px)] bg-size-[16px_16px]" />

      {/* Mobile Gradient Background */}
      <div className="lg:hidden absolute inset-0 z-0 bg-linear-to-br from-indigo-50/50 via-background to-blue-50/30 dark:from-indigo-950/30 dark:via-background dark:to-blue-900/20 pointer-events-none" />

      {/* Left Panel - Hero */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[50%] relative overflow-hidden bg-zinc-100 dark:bg-slate-900">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage}
            alt="Hero"
            className="w-full h-full object-cover opacity-20 dark:opacity-40 mix-blend-overlay"
          />
          <div className="absolute inset-0 bg-linear-to-br from-indigo-100/80 via-white/50 to-blue-50/50 dark:from-indigo-900/90 dark:via-slate-900/95 dark:to-slate-900/90" />
        </div>

        {/* Animated Gradient Orbs */}
        <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-indigo-500/10 dark:bg-indigo-500/20 blur-[100px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] rounded-full bg-blue-600/10 dark:bg-blue-600/20 blur-[120px]" />

        {/* Content Container */}
        <div className="relative z-10 flex flex-col justify-between p-16 w-full h-full">
          {/* Brand */}
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center">
              <img src={logo} alt="Logo" className="h-10 w-auto" />
            </div>
            <span className="text-zinc-900 dark:text-white text-xl font-bold tracking-tight">
              {portalName}
            </span>
          </div>

          {/* Main Text */}
          <div className="max-w-2xl">
            <div>
              <h1 className="text-4xl xl:text-6xl font-bold text-zinc-900 dark:text-white mb-6 leading-[1.1] tracking-tight">
                {heroTitle1} <br />
                <span className="text-transparent bg-clip-text bg-linear-to-r from-indigo-600 via-blue-600 to-indigo-600 dark:from-indigo-200 dark:via-blue-100 dark:to-indigo-200">
                  {heroTitle2}
                </span>
              </h1>
              <p className="text-lg text-zinc-600 dark:text-slate-300 leading-relaxed max-w-lg font-light">
                {heroSubtitle}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center gap-6 text-zinc-500 dark:text-slate-400 text-sm font-medium">
            <span>{copyrightText}</span>
            <div className="w-1 h-1 rounded-full bg-zinc-400 dark:bg-slate-600" />
            <a
              href="#"
              className="hover:text-zinc-900 dark:hover:text-white transition-colors duration-200"
            >
              {privacyPolicyText}
            </a>
            <a
              href="#"
              className="hover:text-zinc-900 dark:hover:text-white transition-colors duration-200"
            >
              {termsOfServiceText}
            </a>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-12 relative z-10">
        <div className="w-full max-w-[440px] sm:max-w-[480px]">
          <div className="bg-card/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-12 shadow-xl sm:shadow-2xl shadow-indigo-900/5 sm:border border-border/50">
            {/* Form Logo */}
            <div className="flex justify-center mb-6">
              <img src={logo} alt="Logo" className="h-12 sm:h-14 w-auto rounded-[10px]" />
            </div>

            {/* Form Header */}
            <div className="mb-6 sm:mb-8 text-center space-y-2 sm:space-y-3">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
                {formHeaderTitle}
              </h2>
              <p className="text-muted-foreground text-sm sm:text-base">{formHeaderSubtitle}</p>
            </div>

            {/* Form Container */}
            <div>{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
